import z from 'zod';
import { optionSchema, optionSchemaRequired } from '~/features/types';

/**
 * [MCV-1518] The five metadata fields are required when *adding* a facility —
 * which is what the ticket reports — but stay optional when editing one that
 * already exists.
 *
 * `db/migrations/20250903162803_employer_facility_model_updates.sql` added
 * clinical_setting_category, clinical_setting, facility_size and area_type as
 * nullable columns with no backfill and no default, so every facility created
 * before 2025-09-03 has NULL in all four — and the API still writes them
 * through `_optional_string()`, so newer rows can be null too. Requiring them on
 * the edit path would mean none of those records could be re-saved until the
 * metadata was filled in: someone correcting a typo in the NPI would be blocked
 * by fields that were never asked of them when the facility was created.
 *
 * Making it required everywhere is defensible, but it needs a backfill
 * migration first rather than a client-side schema change. That is its own
 * ticket.
 */
export const createEmployerFacilitySchema = (isExistingFacility = false) => {
  const metadata = isExistingFacility ? optionSchema : optionSchemaRequired;

  return z.object({
    id: z.number().optional(),
    facilityName: metadata,
    facilityNpi: z
      .string()
      .regex(/^\d{10}$/, 'Must be 10 digits')
      .or(z.literal('')),
    clinicalSettingCategory: metadata,
    clinicalSetting: metadata,
    facilitySize: metadata,
    areaType: metadata,
    facilityId: z.string().optional(),
    isPrimary: z.boolean().optional(),
  });
};

/** The Add Facility schema: every metadata field required. */
export const employerFacilitySchema = createEmployerFacilitySchema(false);

export type EmployerFacilityValues = z.infer<typeof employerFacilitySchema>;
